import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import './Salon.css';

const getRoleBadgeClass = (role) => {
  switch (role) {
    case 'owner': return 'role-badge role-owner';
    case 'admin': return 'role-badge role-admin';
    default: return 'role-badge role-member';
  }
};

const PtoSettingsTab = ({ salonId }) => {
  const { t } = useLanguage();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingCell, setEditingCell] = useState(null); // { userId, field: 'weeklyHours' | 'allocated' }
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/pto/balances`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { year }
      });
      setMembers(response.data.members);
      setError('');
    } catch (err) {
      console.error('Error fetching PTO balances:', err);
      setError(t('timeOff.pto.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [salonId, year, t]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handlePrevYear = () => setYear(prev => prev - 1);
  const handleNextYear = () => setYear(prev => prev + 1);

  const handleEditClick = (member, field) => {
    setEditingCell({ userId: member.user_id, field });
    const currentValue = field === 'weeklyHours' ? member.weekly_hours : member.allocated_days;
    setEditValue(currentValue !== null && currentValue !== undefined ? String(currentValue) : '0');
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleSave = async (member) => {
    const parsed = parseFloat(editValue);
    if (isNaN(parsed)) {
      alert(editingCell.field === 'weeklyHours' ? t('timeOff.pto.invalidWeeklyHours') : t('timeOff.pto.invalidBalance'));
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (editingCell.field === 'weeklyHours') {
        await axios.put(`/api/salons/${salonId}/weekly-hours/${member.user_id}`, { weekly_hours: parsed }, config);
      } else {
        await axios.put(`/api/salons/${salonId}/pto/allocations/${member.user_id}`, { year, allocated_days: parsed }, config);
      }
      setEditingCell(null);
      fetchMembers();
    } catch (err) {
      console.error('Error saving setting:', err);
      alert(err.response?.data?.error || t('timeOff.pto.failedToUpdateBalance'));
    } finally {
      setSaving(false);
    }
  };

  const renderEditableCell = (member, field, displayValue, step) => {
    const isEditing = editingCell?.userId === member.user_id && editingCell?.field === field;
    if (isEditing) {
      return (
        <div className="table-actions">
          <input
            type="number"
            step={step}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="pto-balance-input"
            autoFocus
          />
          <button className="action-button edit" onClick={() => handleSave(member)} disabled={saving} title={t('common.save')}>✓</button>
          <button className="action-button delete" onClick={handleCancelEdit} disabled={saving} title={t('common.cancel')}>✕</button>
        </div>
      );
    }
    return (
      <div className="table-actions">
        <span>{displayValue}</span>
        <button className="action-button edit" onClick={() => handleEditClick(member, field)} title={t('common.edit')}>✏️</button>
      </div>
    );
  };

  return (
    <div>
      <div className="time-off-month-nav">
        <div className="time-off-month-controls">
          <button className="pagination-btn" onClick={handlePrevYear}>‹</button>
          <span className="time-off-month-label">{year}</span>
          <button className="pagination-btn" onClick={handleNextYear}>›</button>
        </div>
      </div>

      <div className="table-header">
        <h4>{t('timeOff.tabs.ptoSettings')}</h4>
      </div>

      {loading ? (
        <div className="loading">{t('common.loading')}...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="table-container">
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('timeOff.pto.employee')}</th>
                  <th>{t('common.type')}</th>
                  <th>{t('timeOff.pto.weeklyHours')}</th>
                  <th>{t('timeOff.pto.allocatedDays')}</th>
                  <th>{t('timeOff.pto.carriedOverDays')}</th>
                  <th>{t('timeOff.pto.spentDays')}</th>
                  <th>{t('timeOff.pto.availableDays')}</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.user_id}>
                    <td>{member.first_name} {member.last_name}</td>
                    <td><span className={getRoleBadgeClass(member.role)}>{member.role}</span></td>
                    <td>
                      {renderEditableCell(
                        member,
                        'weeklyHours',
                        member.weekly_hours === null || member.weekly_hours === undefined ? t('timeOff.pto.notSet') : `${member.weekly_hours}h`,
                        '0.5'
                      )}
                    </td>
                    <td>
                      {renderEditableCell(
                        member,
                        'allocated',
                        member.allocated_days === null || member.allocated_days === undefined ? t('timeOff.pto.notSet') : member.allocated_days,
                        '0.5'
                      )}
                    </td>
                    <td>{member.carried_over_days || 0}</td>
                    <td>{member.spent_days}</td>
                    <td>
                      {member.available_days === null || member.available_days === undefined
                        ? '—'
                        : member.available_days}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PtoSettingsTab;
